import React, { useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Pencil, GitCompare, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAutoSync } from "../hooks/useAutoSync";
import { money } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import MealBuilder from "../components/MealBuilder";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Meals() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [meals, setMeals] = useState([]);
  const [foods, setFoods] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");

  const load = useCallback(async () => {
    const [m, f] = await Promise.all([api.get("/meals"), api.get("/foods")]);
    setMeals(m.data); setFoods(f.data);
  }, []);
  useAutoSync(load, [load]);

  const duplicate = async (id) => { await api.post(`/meals/${id}/duplicate`); toast.success("Meal duplicated — now substitute ingredients"); load(); };
  const del = async (id) => { await api.delete(`/meals/${id}`); toast.success("Meal deleted"); load(); };

  const mealById = (id) => meals.find((m) => m.id === id);
  const a = mealById(cmpA), b = mealById(cmpB);
  const delta = a && b ? b.total_cost - a.total_cost : null;

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Meals</h1>
          <p className="text-sm text-slate-500">{meals.length} reusable meals · uses latest prices automatically</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="build-meal-button"
          className="rounded-full bg-brand px-5 hover:bg-brand-hover"><Plus className="mr-1.5 h-4 w-4" />Build a Meal</Button>
      </div>

      {/* Compare */}
      {meals.length >= 2 && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="meal-compare-panel">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <GitCompare className="h-4 w-4 text-brand" />Compare meal cost
          </div>
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <Select value={cmpA} onValueChange={setCmpA}>
              <SelectTrigger className="flex-1" data-testid="compare-a-select"><SelectValue placeholder="Meal A" /></SelectTrigger>
              <SelectContent>{meals.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
            <ArrowRight className="mx-auto hidden h-4 w-4 text-slate-300 sm:block" />
            <Select value={cmpB} onValueChange={setCmpB}>
              <SelectTrigger className="flex-1" data-testid="compare-b-select"><SelectValue placeholder="Meal B" /></SelectTrigger>
              <SelectContent>{meals.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {a && b && (
            <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-50 p-3" data-testid="compare-result">
              <div className="text-sm">
                <span className="font-mono font-semibold">{money(a.total_cost, cur)}</span>
                <span className="text-slate-400"> vs </span>
                <span className="font-mono font-semibold">{money(b.total_cost, cur)}</span>
              </div>
              <div className={`font-mono font-bold ${delta > 0 ? "text-rose-600" : delta < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                {delta === 0 ? "Same cost" : `${delta > 0 ? "+" : "−"}${money(Math.abs(delta), cur).replace(/^./, (s) => s)} ${delta > 0 ? "more" : "less"}`}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        {meals.map((m) => (
          <div key={m.id} data-testid={`meal-card-${m.id}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:border-brand/40 hover:shadow-lg hover:shadow-brand/5">
            <div className="flex items-start justify-between">
              <h3 className="font-display text-lg font-bold">{m.name}</h3>
              <span className="font-mono text-xl font-extrabold text-brand" data-testid={`meal-cost-${m.id}`}>{money(m.total_cost, cur)}</span>
            </div>
            <div className="mt-3 space-y-1.5">
              {m.ingredients.map((ing, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{ing.name} <span className="text-slate-400">· {ing.amount} {ing.unit}</span></span>
                  <span className="font-mono text-slate-500">{ing.cost != null ? money(ing.cost, cur) : "—"}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-1 border-t border-slate-100 pt-3">
              <button onClick={() => { setEditing(m); setOpen(true); }} data-testid={`edit-meal-${m.id}`}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" />Edit</button>
              <button onClick={() => duplicate(m.id)} data-testid={`duplicate-meal-${m.id}`}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"><Copy className="h-3.5 w-3.5" />Duplicate</button>
              <button onClick={() => del(m.id)} data-testid={`delete-meal-${m.id}`}
                className="ml-auto rounded-lg px-2 py-1 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {meals.length === 0 && (
        <div className="mt-16 text-center text-slate-400">
          <p className="text-sm">No meals yet. Tap <span className="font-semibold text-brand">Build a Meal</span>.</p>
        </div>
      )}

      <MealBuilder open={open} onOpenChange={setOpen} meal={editing} foods={foods} onSaved={load} />
    </div>
  );
}
